import { useParams, useLocation } from "react-router-dom";
import { DiaEditorSaveArgs, useDiaEditor } from "../../components/DiaEditor";
import {
    Campaign, CreativeStudioEntry, CreateCampaignInput, CreateCampaignMutationMutation,
    CreateCampaignMutationMutationVariables,
    GetCampaignQueryQueryResult, GetCampaignQueryQueryVariables,
    UpdateCampaignInput, UpdateCampaignMutationMutation,
    UpdateCampaignMutationMutationVariables,
    UpdateDigitalBookingInput,
    UpdatePanelBookingInput,
    useCreateCampaignMutationMutation,
    useGetCampaignQueryQuery,
    useUpdateCampaignMutationMutation
} from "../../generated/graphql";
import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { useUserContext } from "../../contexts/user-context";
import { NonIdealState } from "../../components/NonIdealState";
import { CampaignForm } from "./form";
import WaitSpinner from "../../components/wait-spinner";
import * as yup from "yup";
import { campaignFieldsToTabs } from "./fieldsToTabs";
import { campaignComponentProject } from "./fieldMappings";
import { useDaybookContext } from "../../contexts/Daybook/DayBookContext";
import { CampaignContextProvider } from "../../contexts/campaign-context";
import { formatISO } from "date-fns";



const isoDateReg = /\d{4}-[01]\d-[0-3]\d/;
// The full wire shape the backend DateTime scalar parses (Y-m-d H:i:s). The
// date-and-time control cannot emit anything else, so this is a belt-and-braces
// check on the composed value only — no per-part time-format rules are needed.
const isoDateTimeReg = /^\d{4}-[01]\d-[0-3]\d ([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;

type CampaignTransient = {
    auto_upgrade: boolean,
    tempFiles: any[]
};

const baseValidationSchema = yup.object().shape({
    name: yup.string().min(3),
    start_date: yup.string().matches(isoDateReg).required(),
    completion_date: yup.string().matches(isoDateReg).required(),
    clientCompanyContact: yup.object().required(),
    clientCompany: yup.object().required(),
    clientSubType: yup.object().required(),
    currency: yup.object().required(),
    salesTeam: yup.object().required(),
    sub_teams_scotland: yup.string()
        .nullable()
        .when('salesTeam', {
            is: (salesTeam) => salesTeam?.prefix === 'BHS', // Only validate if salesTeam.prefix is 'BHS'
            then: yup.string().required(),
            otherwise: yup.string().nullable(),
        }),
    ourContact: yup.object().required(),
    areaBookings: yup.array().of(yup.object().shape({
        regionType: yup.object().required(),
        posterSize: yup.object().required(),
        areaBookingItems: yup.array().of(yup.object().shape({
            campaignComponent: yup.object().shape({
                region: yup.object().required()
            })
            //            distributor: yup.object().required()
        }))
    })),
    experientials: yup.array().of(yup.object().shape({
        campaignComponent: yup.object().shape({
            region: yup.object().required(),
            regionType: yup.object().required(),
            unit_value_local: yup.number().min(1),
            qty: yup.number().min(1)
        }),
        experientialType: yup.object().required(),
        start_date: yup.string().min(10),
        end_date: yup.string().min(10),
    })),
    ambients: yup.array().of(yup.object().shape({
        campaignComponent: yup.object().shape({
            region: yup.object().required(),
            regionType: yup.object().required(),
            unit_value_local: yup.number().min(1),
            qty: yup.number().min(1)
        }),
        ambientType: yup.object().required(),
        start_date: yup.string().min(10),
        end_date: yup.string().min(10),
    })),
    mediaProductions: yup.array().of(yup.object().shape({
        campaignComponent: yup.object().shape({
            unit_value_local: yup.number(),
            qty: yup.number().min(1)
        }),
        size: yup.object().required(),
        mediaType: yup.object().required()
    })),
    creativeStudio: yup.array().of(yup.object().shape({
        campaignComponent: yup.object().shape({
            unit_value_local: yup.number().min(1),
            qty: yup.number().min(1)
        }),
        creativeStudioType: yup.object().required(),
        creativeStudioTypeDetail: yup.object().required(),
        start_date: yup.string().min(10),
        end_date: yup.string().min(10)
    })),
    sundries: yup.array().of(yup.object().shape({
        name: yup.string().required(),
        sundryType: yup.object().required()
    })),
    costs: yup.array().of(yup.object().shape({
        invoice_number: yup.string().required(),
        costSupplier: yup.object().required()
    })),
    // Rigging: the two nullable wire datetimes. Not mandatory; when present
    // they must be a full YYYY-MM-DD HH:mm:ss.
    rigging_up_at: yup.string().nullable().matches(isoDateTimeReg, {
        excludeEmptyString: true,
        message: 'Rigging up must be YYYY-MM-DD HH:mm:ss'
    }),
    rigging_down_at: yup.string().nullable().matches(isoDateTimeReg, {
        excludeEmptyString: true,
        message: 'Rigging down must be YYYY-MM-DD HH:mm:ss'
    })
});

// Cross-field rule: when both Rigging datetimes are populated, down must be on
// or after up (lexicographic order matches chronological for Y-m-d H:i:s).
// The result MUST be assigned: yup 0.32 `test()` returns a clone, so calling
// it without keeping the return value registers nothing at all.
// exported for tests: the cross-field registration is silent when broken, so
// the unit test is the only guard against the regression returning.
export const validationSchema = baseValidationSchema.test({
    name: 'rigging-order',
    message: 'Rigging down must be on or after rigging up',
    test(values) {
        const up = values.rigging_up_at;
        const down = values.rigging_down_at;
        // Only check when both are populated.
        if (!up || !down) return true;
        if (down >= up) return true;
        // The error is attached to the down entry so that control is the one
        // highlighted (a bare schema-level error maps to no field). The path is
        // set here rather than in the test config, which the project's
        // @types/yup does not carry.
        return this.createError({ message: 'Rigging down must be on or after rigging up', path: 'rigging_down_at' });
    }
});

const campaignRelationships = {
    'costs': 'create',
    'costs.currency': 'connect',
    'costs.costSupplier': 'connect',
    areaBookings: 'create',
    'regionalAmAllocation': 'connect',
    'areaBookings.areaBookingItems.campaignComponent.region': 'connect',
    'areaBookings.areaBookingItems.campaignComponent.currency': 'connect',
    'areaBookings.posterSize': 'connect',
    'areaBookings.regionType': 'connect',
    'areaBookings.currency': 'connect',
    'areaBookings.areaBookingItems': 'create',
    'areaBookings.areaBookingItems.distributor': 'connect',
    'areaBookings.areaBookingItems.areaBookingZone': 'connect',
    'clientCompany': 'connect',
    'clientCompanyContact': 'connect',
    'clientSubType': 'connect',
    'currency': 'connect',
    'currencies': 'connect',
    'mediaProductions': 'create',
    'mediaProductions.mediaType': 'connect',
    'mediaProductions.size': 'connect',
    'mediaProductions.campaignComponent.currency': 'connect',
    'mediaProductions.campaignComponent.region': 'connect',
    'mediaProductions.campaignComponent.regionType': 'connect',
    'creativeStudio': 'create',
    'creativeStudio.creativeStudioType': 'connect',
    'creativeStudio.campaignComponent.region': 'connect',
    'creativeStudio.campaignComponent.regionType': 'connect',
    'creativeStudio.creativeStudioTypeDetail': 'connect',
    'creativeStudio.campaignComponent.currency': 'connect',
    'creativeStudioEntries': 'create',
    'creativeStudioEntries.campaignComponent.currency': 'connect',
    'experientials.experientialType': 'connect',
    'experientials.campaignComponent.region': 'connect',
    'experientials.campaignComponent.regionType': 'connect',
    'experientials.campaignComponent.currency': 'connect',
    'ambients.ambientType': 'connect',
    'ambients.campaignComponent.region': 'connect',
    'ambients.campaignComponent.currency': 'connect',
    'sundries.sundryType': 'connect',
    'sundries.campaignComponent.regionType': 'connect',
    'sundries.campaignComponent.region': 'connect',
    'sundries.campaignComponent.currency': 'connect',
    'ourContact': 'connect',
    'panelBookings': 'create', // trash
    'salesTeam': 'connect',
    'previewTags': 'connect',
    'paymentTerms': 'connect',
    'artworkFiles.distributor': 'connect',
    // owned campaignComponent objects - projected (flattened onto the parent on create)
    'areaBookings.areaBookingItems.campaignComponent': campaignComponentProject,
    'mediaProductions.campaignComponent': campaignComponentProject,
    'creativeStudio.campaignComponent': campaignComponentProject,
    'creativeStudioEntries.campaignComponent': campaignComponentProject,
    'experientials.campaignComponent': campaignComponentProject,
    'ambients.campaignComponent': campaignComponentProject,
    'sundries.campaignComponent': campaignComponentProject,
};

// registered by the Creative Studios pane to transform the full form values
// before they are diffed/submitted (writes subcomponent sums back onto section
// amounts in subcomponents mode, and strips component rows from subcomponents).
let creativeStudioBeforeSave: ((values: any) => any) | null = null;
export function registerCreativeStudioBeforeSave(fn: (values: any) => any) {
    creativeStudioBeforeSave = fn;
}

function cleanGraphqlVariables(variables: UpdateCampaignInput & Record<string, any>, transientValues: CampaignTransient, allValues: any) {
    if (!variables) {
        return null;
    }
    const sections = ['mediaProductions', 'experientials', 'ambients', 'areaBookings', 'panelBookings', 'costs', 'sundries', 'creativeStudio'];
    sections.forEach((key) => {
        if (!variables[key]) {
            variables[key] = {};
        }
    });
    if (variables.id != 'new') {
        variables.auto_upgrade = transientValues.auto_upgrade;
    }
    // write the derived subcomponent sums back onto the section rows (D3)
    if (creativeStudioBeforeSave) {
        const full = creativeStudioBeforeSave(allValues);
        if (full?.creativeStudioEntries) {
            variables.creativeStudioEntries = full.creativeStudioEntries;
        }
    }
    return variables;
}

export function CampaignEditor(): ReactElement {
    const params = useParams();
    const location = useLocation();
    const isCreativeStudio = location.pathname.startsWith('/cs-campaigns');
    const baseUrl = isCreativeStudio ? '/cs-campaigns/' : '/campaigns/';
    const [errorsToTabs, setErrorsToTabs] = useState({});
    const isNew = (params.id == 'new');
    const daybook = useDaybookContext();
    const userContext = useUserContext()
    const user = userContext?.user;
    // per-column access control: only request the estimated/actual fields when
    // the user has the permission, so the BE @hasPermission gate never throws
    const withEstimated = userContext.hasPermission('campaigns.creative_studio_estimated');
    const withActual = userContext.hasPermission('campaigns.creative_studio_actual');
    const query = useGetCampaignQueryQuery({ variables: { ref: params.id, withEstimated, withActual } as GetCampaignQueryQueryVariables, skip: isNew || !daybook });
    const [saveMutation, saveResult] = useUpdateCampaignMutationMutation();
    const [createMutation, createResult] = useCreateCampaignMutationMutation();
    const save = useCallback(({ values }: DiaEditorSaveArgs<Campaign>) => {
        return saveMutation({ variables: { input: values as UpdateCampaignInput, withEstimated, withActual } as UpdateCampaignMutationMutationVariables });
    }, [daybook, saveMutation, withEstimated, withActual]);
    const create = useCallback(({ values }: { values: Campaign }) => {
        return createMutation({ variables: { input: values as CreateCampaignInput, withEstimated, withActual } as CreateCampaignMutationMutationVariables });
    }, [daybook, createMutation, withEstimated, withActual]);
    const getValue = useCallback((result) => {
        return result.data?.campaign ?? null;
    }, []);
    const getSaveValue = useCallback((result) => {
        return result.data?.updateCampaign;
    }, []);
    /*const getCreateValue = useCallback((result)=>{
        return result.data?.createCampaign;
    },[]);*/
    // sales team is the only sales team, otherwise null
    const getCreateRedirect = useCallback(({ data, baseUrl }) => {
        console.log('get redirect from', data, baseUrl + data?.createCampaign?.job_reference);
        userContext.addToShortcuts(data.data.createCampaign.id);
        return baseUrl + data?.data.createCampaign?.job_reference;
    }, [user]);
    const makeBlankValues = useCallback((): Campaign => {
        const campaign: Campaign = {
            id: 'new',
            name: '',
            invoice: '',
            purchase_order: '',
            completion_date: '',
            start_date: '',
            job_reference: '',
            status: 'Draft',
            type: isCreativeStudio ? 'creative_studio' : 'standard',
            notes: '',
            photoPaidTotal: 0,
            salesTeam: null,
            sub_teams_scotland: '',
            discount_percentage: 0,
            discount_final: 0,
            regionalAmAllocation: null,
            posters_received: 'NONE',
            currency: null,
            clientSubType: null,
            clientType: null,
            is_purchase_order_required: true,
            clientCompany: null,
            clientCompanyContact: null,
            ourContact: null,
            ambients: [],
            experientials: [],
            costs: [],
            sundries: [],
            areaBookings: [],
            panelBookings: [],
            mediaProductions: [],
            creativeStudio: [],
            // a new creative-studio campaign opens with the six named sections
            creativeStudioEntries: (isCreativeStudio ? [
                'INSTALLATION', 'CONTINGENCY', 'PROJECT MANAGER', 'MATERIALS', 'INSTALL & DERIGG', 'OTHER'
            ].map((name) => ({
                id: 'new',
                name,
                notes: '',
                mode: 'direct',
                children: [],
                campaignComponent: { unit_value_local: 0, unit_value_estimated_local: 0, unit_value_actual: 0, qty: 1, currency: null }
            })) : []) as unknown as CreativeStudioEntry[],
            authorizationFiles: [],
            popsFiles: [],
            artworkFiles: [],
            invoiceFiles: [],
            media_production_notes: '',
            rigging_up_at: null,
            rigging_down_at: null
        } as Campaign & Record<string, any>;
        const st = user?.salesTeams?.length == 1 ? user.salesTeams[0] : null;
        if (st) {
            campaign.salesTeam = {
                id: st.id, name: st.name
            };
            campaign.currency = {
                id: st?.defaultCurrency?.id as string,
                label: st?.defaultCurrency?.label,
                glyph: st?.defaultCurrency?.glyph
            };
        }
        campaign.ourContact = {
            id: user?.id as string,
            name: user?.name,
            role: user?.role
        };
        return campaign;
    }, [user, isCreativeStudio]);
    function cleanTransients({ setTransient }) {
        // runs after a successful save to clear this flag
        setTransient('auto_upgrade', false);
    }
    const data = useDiaEditor<Campaign, GetCampaignQueryQueryResult, CreateCampaignMutationMutation, UpdateCampaignMutationMutation, CampaignTransient>
        ({
            save,
            saveResult,
            create,
            createResult: createResult,
            query: query,
            relationships: campaignRelationships,
            baseUrl: baseUrl,
            blankValues: makeBlankValues,
            label: 'Campaign',
            permission: 'campaign',
            afterSave: cleanTransients,
            transientValues: {
                auto_upgrade: false,
                tempFiles: []
            },
            validationSchema: validationSchema,
            getValue: getValue,
            getSaveValue: getSaveValue,
            getCreateRedirect: getCreateRedirect,
            allowRedirectWhen: (newPath, ref) => {
                console.log('check', newPath, ref, newPath.startsWith(baseUrl + ref));
                return (newPath.startsWith(baseUrl + ref));
            },
            cleanGraphqlVariables
        });
    console.log('EDITOR RENDER', data.form?.values?.currencies, query?.data);
    // parse errors
    useEffect(() => {
        if (data?.form?.values?.id) {
            data.form.setTransient('auto_upgrade', false);
        }
    }, [data?.form?.values?.id]);

    useEffect(() => {
        if (!data.form.errors) {
            if (errorsToTabs && Object.keys(errorsToTabs).length) {
                setErrorsToTabs(null);
                return;
            }
            return;
        }
        setErrorsToTabs(campaignFieldsToTabs(data.form.errors));
    }, [data.form.errors]);

    if (query.error) {
        return <NonIdealState
            severity="error"
            title="An error has occurred"
            message="GraphQL error encountered"
        />;
    }
    if (data.form.status == 'loading' || !daybook) {
        console.log('LOADING REASON', data.form.status, daybook);
        return <WaitSpinner variant="panel" />;
    }
    console.log('LOADING', query.loading, query.data, data.form.values);
    const dt = formatISO(new Date(), { representation: 'date' })
    return <>{data.confirmation}<CampaignContextProvider campaign_start_date={data.form.values.start_date} campaign_created_at={data.form.values.created_at ?? dt}><CampaignForm diaForm={data.form} tabErrors={errorsToTabs} waiting={query.loading} /></CampaignContextProvider></>;
}